import type { AuditDoc, AuditSummary } from '@/types/models';
import { Cpu, MemoryStick, Zap, HardDrive, Network, Database, Boxes, Activity } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { labelClassName } from '@/components/ui/label';
import { useT } from '@/lib/i18n';

export function OverviewTab({ audit }: { audit: AuditDoc }) {
  const { t } = useT();
  const s: Partial<AuditSummary> = audit.summary ?? {};

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          icon={<Cpu className="h-4 w-4" />}
          label={t('audit.kpi.cpu')}
          value={s.cpuModel ?? '—'}
          sub={s.cpuCores ? t('audit.kpi.cores', { n: s.cpuCores }) : undefined}
        />
        <StatCard
          icon={<MemoryStick className="h-4 w-4" />}
          label={t('audit.kpi.ram')}
          value={s.ramGb != null ? `${s.ramGb} GB` : '—'}
        />
        <StatCard
          icon={<Zap className="h-4 w-4" />}
          label={t('audit.kpi.gpu')}
          value={s.gpuCount != null ? `${s.gpuCount}` : '—'}
          sub={s.vramGb != null ? t('audit.kpi.vram', { n: s.vramGb }) : undefined}
        />
        <StatCard
          icon={<HardDrive className="h-4 w-4" />}
          label={t('audit.kpi.storage')}
          value={s.storageGb != null ? `${s.storageGb} GB` : '—'}
        />
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          icon={<Network className="h-4 w-4" />}
          label={t('audit.kpi.nics')}
          value={s.networkInterfaces?.toString() ?? '—'}
        />
        <StatCard
          icon={<Boxes className="h-4 w-4" />}
          label={t('audit.kpi.containers')}
          value={s.containers?.toString() ?? '—'}
        />
        <StatCard
          icon={<Database className="h-4 w-4" />}
          label={t('audit.kpi.data_sources')}
          value="—"
        />
        <StatCard
          icon={<Activity className="h-4 w-4" />}
          label={t('audit.kpi.ai_readiness')}
          value={s.aiReadinessScore != null ? `${s.aiReadinessScore}/100` : '—'}
          accent
        />
      </div>
    </div>
  );
}

function StatCard({ icon, label, value, sub, accent }: { icon: React.ReactNode; label: string; value: string; sub?: string; accent?: boolean }) {
  return (
    <Card>
      <div className="flex items-center justify-between">
        <div className={labelClassName()}>{label}</div>
        <div className={`grid h-7 w-7 place-items-center rounded-md ${accent ? 'bg-brand-100 text-brand-600 dark:bg-brand-900/30 dark:text-brand-300' : 'bg-slate-100 text-slate-500 dark:bg-secondary dark:text-muted-foreground'}`}>{icon}</div>
      </div>
      <div className="mt-2 truncate font-display text-xl text-ink-900 dark:text-white" title={value}>{value}</div>
      {sub && <div className="mt-0.5 text-xs text-ink-500">{sub}</div>}
    </Card>
  );
}
